import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';

interface ProductImageGalleryProps {
  images: Array<{
    url: string;
    altText: string | null;
  }>;
  productTitle: string;
}

export const ProductImageGallery = ({ images, productTitle }: ProductImageGalleryProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: 'start' });
  const [currentIndex, setCurrentIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrentIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  if (!images || images.length === 0) {
    return (
      <div className="aspect-square md:aspect-[3/4] bg-secondary/10 rounded-lg overflow-hidden">
        <div className="w-full h-full flex items-center justify-center">
          <span className="text-muted-foreground">No image available</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="overflow-hidden rounded-lg" ref={emblaRef}>
        <div className="flex">
          {images.map((image, index) => (
            <div
              key={index}
              className="flex-[0_0_100%] min-w-0 aspect-square md:aspect-[3/4]"
            >
              <img
                src={image.url}
                alt={image.altText || `${productTitle} - Image ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Progress Line Indicator */}
      {images.length > 1 && (
        <div className="flex gap-1 mt-3 px-1">
          {images.map((_, index) => (
            <div
              key={index}
              className="flex-1 h-0.5 rounded-full transition-all duration-300"
              style={{
                backgroundColor: index === currentIndex ? 'hsl(var(--foreground))' : 'hsl(var(--muted))',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};
